
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function SandownPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/69bcc430e180054d2cb5a2f00f521458.jpg"
          alt="Professional Painting Services in Sandown"
          className="absolute inset-0 w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Professional Painting Services in Sandown
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Premium painting solutions for Sandown's exclusive residential estates and luxury properties
          </p>
          <Link
            to="/contact"
            className="inline-block bg-yellow-400 text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Expert Painting Services for Sandown's Prestigious Properties
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Sandown represents one of Cape Town's most sought-after residential areas, characterized by its exclusive estates, modern architectural designs, and sophisticated property developments. This upscale neighborhood demands painting services that match its premium standards, combining technical excellence with aesthetic refinement to maintain and enhance property values in this distinguished community.
              </p>
              <p>
                Our specialized painting services in Sandown cater to the unique requirements of luxury residential properties, contemporary estates, and high-end developments. We understand that Sandown homeowners expect nothing less than perfection, which is why our team delivers meticulous attention to detail, premium materials, and flawless finishes that complement the area's sophisticated character and architectural excellence.
              </p>
              <p>
                Working in Sandown requires an understanding of modern architectural trends, premium finish requirements, and the expectations of discerning property owners. Our experienced team has completed numerous projects throughout this exclusive neighborhood, earning a reputation for reliability, professionalism, and exceptional quality that meets the exacting standards of Sandown's residential community.
              </p>
              <p>
                From contemporary family homes to luxury estates and modern architectural masterpieces, we provide comprehensive painting solutions that enhance property aesthetics, protect valuable investments, and deliver lasting results. Our commitment to excellence ensures that every Sandown property receives the premium treatment it deserves, with finishes that reflect the area's prestigious status and sophisticated lifestyle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our Sandown Painting Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Luxury Residential Painting',
                description: 'Premium interior and exterior painting for Sandown\'s exclusive homes with designer color consultations and flawless execution.',
                features: ['Designer color matching', 'Premium paint brands', 'Smooth finish guarantee', 'Minimal disruption service']
              },
              {
                title: 'Contemporary Estate Solutions',
                description: 'Specialized painting for modern architectural properties featuring clean lines, bold colors, and sophisticated design elements.',
                features: ['Modern color palettes', 'Architectural feature painting', 'Precision edge work', 'Contemporary finishes']
              },
              {
                title: 'Interior Design Painting',
                description: 'Collaborate with interior designers to create stunning living spaces with custom colors, textures, and premium finishes.',
                features: ['Designer collaboration', 'Custom color creation', 'Texture applications', 'Feature wall expertise']
              },
              {
                title: 'Exterior Protection Systems',
                description: 'Advanced exterior painting with weather-resistant coatings designed for Cape Town\'s climate and Sandown\'s exposure.',
                features: ['UV-resistant coatings', 'Weather protection', 'Fade-resistant colors', 'Long-lasting durability']
              },
              {
                title: 'Estate Maintenance Programs',
                description: 'Ongoing maintenance services to keep Sandown properties looking pristine with scheduled touch-ups and preventive care.',
                features: ['Annual inspections', 'Preventive maintenance', 'Priority scheduling', 'Touch-up services']
              },
              {
                title: 'New Development Painting',
                description: 'Professional painting for new construction and development projects in Sandown with quality assurance and timely completion.',
                features: ['New build expertise', 'Quality control systems', 'Timeline adherence', 'Developer partnerships']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-yellow-400 text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Understanding Sandown's Unique Painting Requirements
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Modern Architecture</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sandown's contemporary architectural landscape features clean lines, bold design elements, and sophisticated color schemes that require precision painting techniques. Our team specializes in achieving the sharp edges, smooth surfaces, and flawless finishes that modern architecture demands, ensuring every detail meets the highest aesthetic standards.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We work with architects and designers to understand design intent, select appropriate finishes, and execute painting projects that enhance architectural features while maintaining the integrity of contemporary design principles throughout Sandown's exclusive properties.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Premium Standards</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sandown homeowners expect exceptional quality, attention to detail, and professional service that matches their property investments. We deliver premium painting solutions using top-tier materials, experienced craftsmen, and rigorous quality control processes that ensure every project exceeds expectations and maintains the area's prestigious reputation.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our commitment to excellence includes thorough surface preparation, multiple quality inspections, and meticulous finishing work that creates lasting results worthy of Sandown's distinguished residential community and sophisticated property standards.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Climate Considerations</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cape Town's diverse weather patterns, including strong winds, intense UV exposure, and seasonal rainfall, require specialized exterior painting systems that protect and preserve Sandown properties. We utilize advanced weather-resistant coatings, UV-stable pigments, and moisture management techniques that ensure long-lasting protection and color retention.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our climate-specific approach includes proper surface sealing, breathable coating systems, and strategic application timing that maximizes paint performance and durability in Sandown's environmental conditions, protecting your investment for years to come.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Minimal Disruption</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We understand that Sandown residents value their privacy, comfort, and daily routines. Our professional approach includes flexible scheduling, efficient project management, and respectful work practices that minimize disruption while delivering exceptional results within agreed timelines.
              </p>
              <p className="text-gray-700 leading-relaxed">
                From careful site protection to thorough daily cleanup, we maintain the highest standards of professionalism and consideration, ensuring your painting project proceeds smoothly without compromising your lifestyle or property enjoyment throughout the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our Sandown Painting Process
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Property Consultation',
                description: 'Comprehensive on-site assessment of your Sandown property with detailed discussion of your vision, requirements, and expectations for the project.'
              },
              {
                step: '02',
                title: 'Design & Planning',
                description: 'Professional color consultation, material selection, and project planning tailored to your property\'s architecture and your aesthetic preferences.'
              },
              {
                step: '03',
                title: 'Detailed Quotation',
                description: 'Transparent, itemized proposal outlining scope, timeline, materials, and costs with no hidden fees or surprises throughout the project.'
              },
              {
                step: '04',
                title: 'Surface Preparation',
                description: 'Meticulous preparation including cleaning, repairs, priming, and protection to ensure optimal paint adhesion and flawless final results.'
              },
              {
                step: '05',
                title: 'Expert Application',
                description: 'Professional painting by experienced craftsmen using premium materials and proven techniques that deliver exceptional quality and durability.'
              },
              {
                step: '06',
                title: 'Quality Assurance',
                description: 'Thorough inspection, touch-ups, and final walkthrough to ensure complete satisfaction and guarantee that every detail meets our exacting standards.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="text-5xl font-bold text-yellow-400 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Sandown Homeowners Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Premium Quality Guarantee',
                description: 'We use only the finest paints and materials from leading brands, combined with expert craftsmanship that ensures exceptional results and long-lasting beauty for your Sandown property.'
              },
              {
                icon: 'ri-team-line',
                title: 'Experienced Professionals',
                description: 'Our skilled team brings years of experience working on luxury properties throughout Sandown, understanding the unique requirements and high standards expected in this prestigious neighborhood.'
              },
              {
                icon: 'ri-time-line',
                title: 'Reliable & Punctual',
                description: 'We respect your time and schedule, arriving promptly, working efficiently, and completing projects within agreed timelines while maintaining the highest quality standards throughout.'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Exceptional Service',
                description: 'From initial consultation to final inspection, we provide professional, courteous service that prioritizes your satisfaction, addresses your concerns, and exceeds your expectations at every stage.'
              },
              {
                icon: 'ri-paint-brush-line',
                title: 'Attention to Detail',
                description: 'Every project receives meticulous attention to detail, from precise edge work to smooth finishes, ensuring flawless results that enhance your property\'s beauty and value.'
              },
              {
                icon: 'ri-hand-heart-line',
                title: 'Complete Satisfaction',
                description: 'Your complete satisfaction is our ultimate goal. We stand behind our work with comprehensive guarantees and remain committed to delivering results that make you proud of your Sandown home.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Recent Sandown Projects
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Contemporary Family Estate',
                description: 'Complete interior and exterior transformation of a modern 5-bedroom family home featuring custom color palette, premium finishes, and architectural feature painting that enhanced the property\'s contemporary design.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20luxury%20family%20home%20in%20Sandown%20Cape%20Town%20with%20contemporary%20architecture%2C%20freshly%20painted%20white%20and%20grey%20exterior%2C%20manicured%20garden%2C%20large%20windows%2C%20sophisticated%20design%2C%20professional%20finish&width=800&height=600&seq=sandown-project-001&orientation=landscape'
              },
              {
                title: 'Luxury New Development',
                description: 'Professional painting for a prestigious new residential development including 12 luxury units with coordinated color schemes, premium interior finishes, and weather-resistant exterior coatings meeting developer specifications.',
                image: 'https://readdy.ai/api/search-image?query=New%20luxury%20residential%20development%20in%20Sandown%20Cape%20Town%2C%20modern%20apartment%20complex%20with%20fresh%20paint%2C%20contemporary%20architecture%2C%20professional%20finish%2C%20upscale%20neighborhood%20setting%2C%20pristine%20condition&width=800&height=600&seq=sandown-project-002&orientation=landscape'
              },
              {
                title: 'Architectural Masterpiece',
                description: 'Specialized painting for an award-winning contemporary residence featuring complex architectural elements, bold color contrasts, and precision finishing that highlighted the property\'s unique design features and artistic vision.',
                image: 'https://readdy.ai/api/search-image?query=Architectural%20masterpiece%20home%20in%20Sandown%20Cape%20Town%20with%20unique%20modern%20design%2C%20bold%20contemporary%20colors%2C%20geometric%20features%2C%20professional%20painting%2C%20luxury%20property%2C%20artistic%20architecture&width=800&height=600&seq=sandown-project-003&orientation=landscape'
              },
              {
                title: 'Estate Renovation Project',
                description: 'Comprehensive exterior and interior renovation painting for an established Sandown estate including surface restoration, color modernization, and protective coating systems that revitalized the property while preserving its character.',
                image: 'https://readdy.ai/api/search-image?query=Renovated%20luxury%20estate%20in%20Sandown%20Cape%20Town%20with%20refreshed%20paint%2C%20elegant%20exterior%2C%20modern%20color%20scheme%2C%20beautiful%20gardens%2C%20professional%20restoration%2C%20upscale%20residential%20property&width=800&height=600&seq=sandown-project-004&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="w-full h-80">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{project.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            What Sandown Clients Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Michael & Sarah Thompson',
                location: 'Sandown Estate',
                text: 'Exceptional quality and professionalism from start to finish. Our contemporary home looks absolutely stunning, and the attention to detail was remarkable. Highly recommend their services to any Sandown homeowner.'
              },
              {
                name: 'David Chen',
                location: 'Sandown Residence',
                text: 'The team transformed our property with beautiful results that exceeded our expectations. Professional, reliable, and meticulous in their work. The perfect choice for luxury home painting in Sandown.'
              },
              {
                name: 'Jennifer Williams',
                location: 'Sandown Property',
                text: 'Outstanding service and beautiful finishes throughout our home. The team was respectful, efficient, and delivered flawless results. Our property has never looked better, and we couldn\'t be happier.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Sandown Property?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Contact us today for a free consultation and discover how our premium painting services can enhance your home's beauty and value.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-yellow-400 text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer"
          >
            Get Your Free Quote Today
          </Link>
        </div>
      </section>
    </div>
  );
}
